(function () {
  'use strict';

  const SELECTORS = {
    gridContainer: '#ProductGridContainer',
    productGrid: '#product-grid',
    filterGroups: '#cf-filter-groups',
    activeFilters: '#cf-active-filters',
    productCount: '#cf-product-count',
    clearAll: '#cf-clear-all',
    sidebar: '#custom-filter-sidebar',
    filterToggle: '#cf-filter-toggle',
    searchInput: '#cf-search-input',
    sortSelect: '#cf-sort-select',
  };

  let allProducts = [];
  let activeFilters = {}; // { groupName: Set([values]) }
  let gridEl = null;
  let totalCount = 0;
  let currentSort = 'default';
  let searchQuery = '';
  let parallaxInitialized = false;
  let colorGroups = {};      // { "ProductTitle": { "ColorName": "Group", ... }, ... }
  let colorGroupMode = true; // true = show grouped names, false = show individual color names

  function getColorGroup(productTitle, colorValue) {
    var productMap = colorGroups[productTitle];
    return (productMap && productMap[colorValue]) || colorValue;
  }
  let cachedFilterData = {}; // stores both grouped and ungrouped color counts
  let sizeOrder = [];        // ordered list of size display names
  let sizeGroups = {};       // { "24x48": ["24x48", "24x48 Groove Deco"], ... }
  let sizeToGroup = {};      // { "24x48 Groove Deco": "24x48", ... }

  // ===== Init =====

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    const container = document.querySelector(SELECTORS.gridContainer);
    if (!container) return;

    const handle = container.dataset.collectionHandle;
    if (!handle) return;

    gridEl = document.querySelector(SELECTORS.productGrid);
    if (!gridEl) return;

    // Show loading state in filter groups
    const filterGroupsEl = document.querySelector(SELECTORS.filterGroups);
    if (filterGroupsEl) {
      filterGroupsEl.innerHTML =
        '<div class="cf-loading"><div class="cf-loading-spinner"></div><div>Loading filters...</div></div>';
    }

    // Set up collapsible filter toggle
    setupFilterToggle();

    // Set up clear all
    const clearBtn = document.querySelector(SELECTORS.clearAll);
    if (clearBtn) {
      clearBtn.addEventListener('click', clearAllFilters);
    }

    // Set up sort dropdown
    var sortEl = document.querySelector(SELECTORS.sortSelect);
    if (sortEl) {
      sortEl.addEventListener('change', function () {
        currentSort = sortEl.value;
        applyFilters();
      });
    }

    // Set up search input with debounce
    var searchEl = document.querySelector(SELECTORS.searchInput);
    if (searchEl) {
      var debounceTimer = null;
      searchEl.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          searchQuery = searchEl.value.trim().toLowerCase();
          applyFilters();
        }, 200);
      });
    }

    // Fetch color groups config + size order config + all products in parallel
    var colorGroupsPromise;
    if (window.__colorGroupsData && Object.keys(window.__colorGroupsData).length > 0) {
      colorGroupsPromise = Promise.resolve(window.__colorGroupsData);
    } else {
      var colorGroupsUrl = window.__colorGroupsUrl || ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cdn/shop/t/1/assets/color-groups.json');
      colorGroupsPromise = fetch(colorGroupsUrl).then(function (r) { return r.ok ? r.json() : {}; }).catch(function () { return {}; });
    }

    var sizeOrderPromise;
    var sizeOrderUrl = window.__sizeOrderUrl;
    if (sizeOrderUrl) {
      sizeOrderPromise = fetch(sizeOrderUrl).then(function (r) { return r.ok ? r.json() : {}; }).catch(function () { return {}; });
    } else {
      sizeOrderPromise = Promise.resolve({});
    }

    Promise.all([
      fetchAllProducts(handle),
      colorGroupsPromise,
      sizeOrderPromise
    ]).then(function (results) {
      var products = results[0];
      colorGroups = results[1] || {};
      var sizeConfig = results[2] || {};

      // colorGroups is now { productTitle: { colorName: groupName } }
      // Lookup helper: getColorGroup(productTitle, colorValue) → groupName or colorValue

      // Build size ordering + grouping lookups
      sizeOrder = sizeConfig.order || [];
      sizeGroups = sizeConfig.groups || {};
      sizeToGroup = {};
      Object.keys(sizeGroups).forEach(function (displayName) {
        sizeGroups[displayName].forEach(function (rawSize) {
          if (rawSize !== displayName) {
            sizeToGroup[rawSize] = displayName;
          }
        });
      });

      // Tag each product with original index for stable reference
      products.forEach(function (p, i) { p._originalIndex = i; });
      allProducts = products;
      totalCount = products.length;
      buildFilterGroups(products);
      rebuildGrid(products);
      initParallax();
      initSwatchPreview();
      updateProductCount();

      // Auto-apply filters from URL parameters (e.g., ?look=marble)
      applyUrlParamFilters();
    });
  }

  // ===== Fetch Products =====

  function fetchAllProducts(handle) {
    var products = [];
    var page = 1;

    var MAX_PAGES = 10;

    function fetchPage(pg) {
      return fetch('/collections/' + handle + '/products.json?limit=250&page=' + pg)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.products && data.products.length > 0) {
            products = products.concat(data.products);
            if (data.products.length === 250 && pg < MAX_PAGES) {
              return fetchPage(pg + 1);
            }
          }
          return products;
        })
        .catch(function (err) {
          console.warn('Failed to fetch products page ' + pg + ':', err);
          return products;
        });
    }

    return fetchPage(page);
  }

  // ===== Build Filter Groups =====

  function buildFilterGroups(products) {
    var groupsGrouped = {};
    var groupsIndividual = {};

    var hasColorGroups = Object.keys(colorGroups).length > 0;
    var hasSizeGroups = Object.keys(sizeGroups).length > 0;

    // Extract option-based groups — build both grouped and individual versions
    products.forEach(function (product) {
      if (product.options) {
        product.options.forEach(function (option) {
          var name = option.name;
          var isColor = ['color', 'colour', 'colore'].indexOf(name.toLowerCase()) !== -1;
          var isSize = name.toLowerCase() === 'size';

          if (!groupsGrouped[name]) groupsGrouped[name] = {};
          if (!groupsIndividual[name]) groupsIndividual[name] = {};

          option.values.forEach(function (val) {
            if (val && val !== 'Default Title') {
              // Individual (always raw values)
              if (!groupsIndividual[name][val]) groupsIndividual[name][val] = 0;
              groupsIndividual[name][val]++;

              // Grouped (map to group if color or size)
              if (isColor && hasColorGroups) {
                var colorGroup = getColorGroup(product.title, val);
                if (!groupsGrouped[name][colorGroup]) groupsGrouped[name][colorGroup] = 0;
                groupsGrouped[name][colorGroup]++;
              } else if (isSize && hasSizeGroups) {
                var sizeGroupName = sizeToGroup[val] || val;
                if (!groupsGrouped[name][sizeGroupName]) groupsGrouped[name][sizeGroupName] = 0;
                groupsGrouped[name][sizeGroupName]++;
              } else {
                if (!groupsGrouped[name][val]) groupsGrouped[name][val] = 0;
                groupsGrouped[name][val]++;
              }
            }
          });
        });
      }

      // Product type
      if (product.product_type) {
        if (!groupsGrouped['Type']) groupsGrouped['Type'] = {};
        if (!groupsGrouped['Type'][product.product_type]) groupsGrouped['Type'][product.product_type] = 0;
        groupsGrouped['Type'][product.product_type]++;
        if (!groupsIndividual['Type']) groupsIndividual['Type'] = {};
        if (!groupsIndividual['Type'][product.product_type]) groupsIndividual['Type'][product.product_type] = 0;
        groupsIndividual['Type'][product.product_type]++;
      }

      // Vendor
      if (product.vendor) {
        if (!groupsGrouped['Vendor']) groupsGrouped['Vendor'] = {};
        if (!groupsGrouped['Vendor'][product.vendor]) groupsGrouped['Vendor'][product.vendor] = 0;
        groupsGrouped['Vendor'][product.vendor]++;
        if (!groupsIndividual['Vendor']) groupsIndividual['Vendor'] = {};
        if (!groupsIndividual['Vendor'][product.vendor]) groupsIndividual['Vendor'][product.vendor] = 0;
        groupsIndividual['Vendor'][product.vendor]++;
      }

      // Tag-based filters (tags like "look:Marble", "material:Porcelain")
      var tagPrefixes = { 'look': 'Look', 'material': 'Material' };
      if (product.tags) {
        var tagList = typeof product.tags === 'string' ? product.tags.split(', ') : product.tags;
        tagList.forEach(function (tag) {
          var parts = tag.split(':');
          if (parts.length === 2) {
            var prefix = parts[0].trim().toLowerCase();
            var val = parts[1].trim();
            var label = tagPrefixes[prefix];
            if (label && val) {
              if (!groupsGrouped[label]) groupsGrouped[label] = {};
              if (!groupsGrouped[label][val]) groupsGrouped[label][val] = 0;
              groupsGrouped[label][val]++;
              if (!groupsIndividual[label]) groupsIndividual[label] = {};
              if (!groupsIndividual[label][val]) groupsIndividual[label][val] = 0;
              groupsIndividual[label][val]++;
            }
          }
        });
      }
    });

    // Remove groups with only 1 value
    [groupsGrouped, groupsIndividual].forEach(function (g) {
      Object.keys(g).forEach(function (key) {
        if (Object.keys(g[key]).length <= 1) {
          delete g[key];
        }
      });
    });

    // Cache both versions
    cachedFilterData = { grouped: groupsGrouped, individual: groupsIndividual };

    renderFilterGroups(colorGroupMode ? groupsGrouped : groupsIndividual);
  }

  function renderFilterGroups(groups) {
    var container = document.querySelector(SELECTORS.filterGroups);
    if (!container) return;

    container.innerHTML = '';

    var groupNames = Object.keys(groups);

    // Put option-based groups first, then Type, then Vendor
    var priority = { 'Type': 100, 'Vendor': 101 };
    groupNames.sort(function (a, b) {
      return (priority[a] || 0) - (priority[b] || 0);
    });

    groupNames.forEach(function (groupName) {
      var values = groups[groupName];
      var sortedValues;
      var isSize = groupName.toLowerCase() === 'size';

      if (isSize && sizeOrder.length > 0) {
        // Custom size ordering: ordered items first, then remaining alphabetically
        var orderMap = {};
        sizeOrder.forEach(function (s, i) { orderMap[s] = i; });
        sortedValues = Object.keys(values).sort(function (a, b) {
          var aIdx = orderMap[a] !== undefined ? orderMap[a] : 9999;
          var bIdx = orderMap[b] !== undefined ? orderMap[b] : 9999;
          if (aIdx !== bIdx) return aIdx - bIdx;
          return a.localeCompare(b);
        });
      } else {
        sortedValues = Object.keys(values).sort();
      }

      var groupDiv = document.createElement('div');
      groupDiv.className = 'cf-group';
      groupDiv.dataset.group = groupName;

      // Toggle button
      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'cf-group-toggle';
      toggle.innerHTML =
        '<span>' + escapeHtml(groupName) + '</span>' +
        '<svg class="cf-chevron" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5">' +
        '<path d="M3 4.5L6 7.5L9 4.5"/></svg>';

      toggle.addEventListener('click', function () {
        groupDiv.classList.toggle('is-collapsed');
      });

      groupDiv.appendChild(toggle);

      // Add color mode toggle for color groups
      var isColor = ['color', 'colour', 'colore'].indexOf(groupName.toLowerCase()) !== -1;
      var hasGroups = isColor && Object.keys(colorGroups).length > 0;
      if (hasGroups) {
        var modeLink = document.createElement('button');
        modeLink.type = 'button';
        modeLink.className = 'cf-color-mode-toggle';
        modeLink.textContent = colorGroupMode ? 'Show all colors' : 'Show groups';
        modeLink.addEventListener('click', function () {
          toggleColorGroupMode();
        });
        groupDiv.appendChild(modeLink);
      }

      // Options container
      var optionsDiv = document.createElement('div');
      optionsDiv.className = 'cf-group-options';

      sortedValues.forEach(function (value) {
        var count = values[value];

        var label = document.createElement('label');
        label.className = 'cf-option';

        var checkboxWrap = document.createElement('span');
        checkboxWrap.className = 'cf-checkbox';

        var input = document.createElement('input');
        input.type = 'checkbox';
        input.dataset.group = groupName;
        input.dataset.value = value;
        input.addEventListener('change', onFilterChange);

        var visual = document.createElement('span');
        visual.className = 'cf-checkbox-visual';

        checkboxWrap.appendChild(input);
        checkboxWrap.appendChild(visual);

        var labelSpan = document.createElement('span');
        labelSpan.className = 'cf-option-label';
        labelSpan.textContent = value;

        var countSpan = document.createElement('span');
        countSpan.className = 'cf-option-count';
        countSpan.textContent = '(' + count + ')';

        label.appendChild(checkboxWrap);
        label.appendChild(labelSpan);
        label.appendChild(countSpan);
        optionsDiv.appendChild(label);
      });

      // Set max-height for transition
      groupDiv.appendChild(optionsDiv);
      container.appendChild(groupDiv);

      // Set max-height after render
      requestAnimationFrame(function () {
        optionsDiv.style.maxHeight = optionsDiv.scrollHeight + 'px';
      });
    });
  }

  // ===== Filter Logic =====

  function onFilterChange(e) {
    var group = e.target.dataset.group;
    var value = e.target.dataset.value;

    if (!activeFilters[group]) {
      activeFilters[group] = new Set();
    }

    if (e.target.checked) {
      activeFilters[group].add(value);
    } else {
      activeFilters[group].delete(value);
      if (activeFilters[group].size === 0) {
        delete activeFilters[group];
      }
    }

    applyFilters();
  }

  function toggleColorGroupMode() {
    // Clear any active color filters when switching modes (values won't match across modes)
    Object.keys(activeFilters).forEach(function (groupName) {
      var isColor = ['color', 'colour', 'colore'].indexOf(groupName.toLowerCase()) !== -1;
      if (isColor) delete activeFilters[groupName];
    });

    colorGroupMode = !colorGroupMode;

    // Re-render filter groups with the other data set
    var data = colorGroupMode ? cachedFilterData.grouped : cachedFilterData.individual;
    if (data) {
      renderFilterGroups(data);
      applyFilters();
    }
  }

  function applyFilters() {
    var hasFilters = Object.keys(activeFilters).length > 0;
    var hasSearch = searchQuery.length > 0;

    // === Stage 1: Filter ===
    var filtered = allProducts.filter(function (product) {
      // Search query match (title OR variant SKU)
      if (hasSearch) {
        var titleMatch = product.title.toLowerCase().indexOf(searchQuery) !== -1;
        var skuMatch = false;
        if (product.variants && product.variants.length) {
          for (var si = 0; si < product.variants.length; si++) {
            var sku = product.variants[si].sku;
            if (sku && sku.toLowerCase().indexOf(searchQuery) !== -1) {
              skuMatch = true;
              break;
            }
          }
        }
        if (!titleMatch && !skuMatch) return false;
      }

      // Checkbox filter match (AND across groups, OR within group)
      if (hasFilters) {
        var pass = true;
        Object.keys(activeFilters).forEach(function (groupName) {
          var selectedValues = activeFilters[groupName];
          if (selectedValues.size === 0) return;

          var match = false;

          // Tag-based filter groups (Look, Material)
          var tagFilterPrefixes = { 'Look': 'look', 'Material': 'material' };
          var tagPrefix = tagFilterPrefixes[groupName];

          if (groupName === 'Type') {
            match = selectedValues.has(product.product_type);
          } else if (groupName === 'Vendor') {
            match = selectedValues.has(product.vendor);
          } else if (tagPrefix && product.tags) {
            var tagList = typeof product.tags === 'string' ? product.tags.split(', ') : product.tags;
            tagList.forEach(function (tag) {
              var parts = tag.split(':');
              if (parts.length === 2 && parts[0].trim().toLowerCase() === tagPrefix) {
                if (selectedValues.has(parts[1].trim())) match = true;
              }
            });
          } else {
            if (product.options) {
              var isColorOption = ['color', 'colour', 'colore'].indexOf(groupName.toLowerCase()) !== -1;
              var useColorGroupLookup = isColorOption && colorGroupMode && Object.keys(colorGroups).length > 0;
              var isSizeOption = groupName.toLowerCase() === 'size';
              var useSizeGroupLookup = isSizeOption && Object.keys(sizeGroups).length > 0;

              product.options.forEach(function (option) {
                if (option.name === groupName) {
                  option.values.forEach(function (val) {
                    if (useColorGroupLookup) {
                      // Grouped mode: check if this value's group is selected
                      var grp = getColorGroup(product.title, val);
                      if (selectedValues.has(grp)) match = true;
                    } else if (useSizeGroupLookup) {
                      // Size grouping: check if this value's group is selected
                      var sizeGrp = sizeToGroup[val] || val;
                      if (selectedValues.has(sizeGrp)) match = true;
                    } else {
                      // Individual mode: direct value match
                      if (selectedValues.has(val)) match = true;
                    }
                  });
                }
              });
            }
          }

          if (!match) pass = false;
        });
        if (!pass) return false;
      }

      return true;
    });

    // === Stage 2: Sort ===
    var sorted = filtered.slice(); // clone
    if (currentSort === 'a-z') {
      sorted.sort(function (a, b) { return a.title.localeCompare(b.title); });
    } else if (currentSort === 'z-a') {
      sorted.sort(function (a, b) { return b.title.localeCompare(a.title); });
    } else if (currentSort === 'newest') {
      sorted.sort(function (a, b) {
        return new Date(b.created_at) - new Date(a.created_at);
      });
    } else if (currentSort === 'oldest') {
      sorted.sort(function (a, b) {
        return new Date(a.created_at) - new Date(b.created_at);
      });
    }
    // 'default' keeps original fetch order (by _originalIndex)

    // === Stage 3: Rebuild ===
    rebuildGrid(sorted);

    // No-results message
    var existing = gridEl.querySelector('.cf-no-results');
    if (sorted.length === 0 && (hasFilters || hasSearch)) {
      if (!existing) {
        var noResults = document.createElement('li');
        noResults.className = 'cf-no-results';
        noResults.textContent = 'No products match your criteria.';
        gridEl.appendChild(noResults);
      }
    } else if (existing) {
      existing.remove();
    }

    updateActivePills();
    updateProductCount(sorted.length);
    updateClearButton();
  }

  // ===== Variant Image Swap =====

  function swapCardImage(card, product, optionFilters) {
    var img = card.querySelector('.cf-card__media img');
    if (!img) return;

    // If no option filters active, reset to default image
    if (!optionFilters) {
      var defaultSrc = (product.images && product.images.length > 0) ? product.images[0].src : '';
      if (defaultSrc) {
        var sized = defaultSrc + (defaultSrc.indexOf('?') > -1 ? '&' : '?') + 'width=533';
        if (img.src !== sized) img.src = sized;
      }
      return;
    }

    // Find the best matching variant based on active option filters
    var bestVariant = null;
    var bestScore = 0;

    if (product.variants) {
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        if (!v.featured_image) continue;

        var score = 0;
        var matches = true;

        // Check each active option filter against variant options
        Object.keys(optionFilters).forEach(function (groupName) {
          var selectedValues = optionFilters[groupName];
          // Find which option position this group corresponds to
          for (var oi = 0; oi < product.options.length; oi++) {
            if (product.options[oi].name === groupName) {
              var variantVal = v['option' + (oi + 1)];
              if (variantVal && selectedValues.has(variantVal)) {
                score++;
              } else {
                matches = false;
              }
              break;
            }
          }
        });

        if (score > bestScore) {
          bestScore = score;
          bestVariant = v;
        }
      }
    }

    if (bestVariant && bestVariant.featured_image && bestVariant.featured_image.src) {
      var newSrc = bestVariant.featured_image.src + (bestVariant.featured_image.src.indexOf('?') > -1 ? '&' : '?') + 'width=533';
      if (img.src !== newSrc) img.src = newSrc;
    }
  }

  // ===== Active Pills =====

  function updateActivePills() {
    var container = document.querySelector(SELECTORS.activeFilters);
    if (!container) return;

    container.innerHTML = '';

    Object.keys(activeFilters).forEach(function (group) {
      activeFilters[group].forEach(function (value) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'cf-pill';
        pill.innerHTML =
          escapeHtml(value) +
          ' <svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5">' +
          '<path d="M2 2L8 8M8 2L2 8"/></svg>';

        pill.addEventListener('click', function () {
          removeFilter(group, value);
        });

        container.appendChild(pill);
      });
    });
  }

  function removeFilter(group, value) {
    if (activeFilters[group]) {
      activeFilters[group].delete(value);
      if (activeFilters[group].size === 0) {
        delete activeFilters[group];
      }
    }

    // Uncheck the corresponding checkbox
    var checkbox = document.querySelector(
      'input[data-group="' + CSS.escape(group) + '"][data-value="' + CSS.escape(value) + '"]'
    );
    if (checkbox) checkbox.checked = false;

    applyFilters();
  }

  function clearAllFilters() {
    activeFilters = {};

    // Uncheck all checkboxes
    var checkboxes = document.querySelectorAll('.cf-checkbox input');
    checkboxes.forEach(function (cb) { cb.checked = false; });

    // Reset search
    searchQuery = '';
    var searchEl = document.querySelector(SELECTORS.searchInput);
    if (searchEl) searchEl.value = '';

    // Reset sort
    currentSort = 'default';
    var sortEl = document.querySelector(SELECTORS.sortSelect);
    if (sortEl) sortEl.value = 'default';

    applyFilters();
  }

  // ===== URL Parameter Auto-Filtering =====

  function applyUrlParamFilters() {
    var params = new URLSearchParams(window.location.search);

    // Map URL param names to filter group names
    var paramToGroup = {
      'look': 'Look',
      'material': 'Material',
      'type': 'Type',
      'vendor': 'Vendor'
    };

    var applied = false;

    params.forEach(function (value, key) {
      var groupName = paramToGroup[key.toLowerCase()];
      if (!groupName) return;

      // Split on comma for multi-value support (?look=marble,stone)
      var values = value.split(',');

      values.forEach(function (v) {
        var trimmed = v.trim();
        if (!trimmed) return;

        // Find the matching filter value (case-insensitive)
        var filterData = colorGroupMode ? cachedFilterData.grouped : cachedFilterData.individual;
        if (!filterData || !filterData[groupName]) return;

        var matchedValue = null;
        Object.keys(filterData[groupName]).forEach(function (filterVal) {
          if (filterVal.toLowerCase() === trimmed.toLowerCase()) {
            matchedValue = filterVal;
          }
        });

        if (matchedValue) {
          if (!activeFilters[groupName]) {
            activeFilters[groupName] = new Set();
          }
          activeFilters[groupName].add(matchedValue);

          // Check the corresponding checkbox in the UI
          var checkbox = document.querySelector(
            'input[data-group="' + CSS.escape(groupName) + '"][data-value="' + CSS.escape(matchedValue) + '"]'
          );
          if (checkbox) checkbox.checked = true;

          applied = true;
        }
      });
    });

    if (applied) {
      applyFilters();
    }
  }

  // ===== Product Count =====

  function updateProductCount(visible) {
    var el = document.querySelector(SELECTORS.productCount);
    if (!el) return;

    if (visible === undefined) {
      visible = totalCount;
    }

    if (Object.keys(activeFilters).length > 0 || searchQuery.length > 0 || currentSort !== 'default') {
      el.textContent = 'Showing ' + visible + ' of ' + totalCount + ' products';
    } else {
      el.textContent = totalCount + ' products';
    }
  }

  function updateClearButton() {
    var btn = document.querySelector(SELECTORS.clearAll);
    if (!btn) return;

    btn.hidden = Object.keys(activeFilters).length === 0 && searchQuery.length === 0 && currentSort === 'default';
  }

  // ===== Rebuild Grid =====

  function rebuildGrid(products) {
    if (!gridEl) return;

    gridEl.innerHTML = '';

    products.forEach(function (product) {
      var li = document.createElement('li');
      li.className = 'grid__item';
      li.dataset.productIndex = product._originalIndex !== undefined ? product._originalIndex : 0;

      var productUrl = escapeAttr(product.handle ? '/products/' + product.handle : '#');

      var imgSrc = '';
      var imgAlt = '';
      if (product.images && product.images.length > 0) {
        imgSrc = product.images[0].src;
        imgAlt = product.images[0].alt || product.title;
      }
      if (imgSrc) {
        imgSrc = imgSrc + (imgSrc.indexOf('?') > -1 ? '&' : '?') + 'width=600';
      }

      // Build color swatches HTML
      var swatchesHtml = buildColorSwatches(product);

      // Extract look tag (e.g. "look:Stone" → "Stone Look")
      var lookHtml = '';
      if (product.tags) {
        var tagList = typeof product.tags === 'string' ? product.tags.split(', ') : product.tags;
        for (var ti = 0; ti < tagList.length; ti++) {
          var parts = tagList[ti].split(':');
          if (parts.length === 2 && parts[0].trim().toLowerCase() === 'look') {
            var lookVal = parts[1].trim();
            if (lookVal) {
              lookHtml = '<span class="cf-card__look">' + escapeHtml(lookVal) + ' Look</span>';
            }
            break;
          }
        }
      }

      li.innerHTML =
        '<a href="' + productUrl + '" class="cf-card">' +
          '<div class="cf-card__media">' +
            (imgSrc
              ? '<img src="' + escapeAttr(imgSrc) + '"' +
                  ' alt="' + escapeAttr(imgAlt) + '"' +
                  ' loading="lazy" width="600" height="600">'
              : '') +
          '</div>' +
          '<div class="cf-card__preview"></div>' +
          '<div class="cf-card__overlay">' +
            '<h3 class="cf-card__title">' + escapeHtml(product.title) + '</h3>' +
             swatchesHtml +
          '</div>' +
          lookHtml +
        '</a>';

      gridEl.appendChild(li);
    });
  }

  // ===== Filter Toggle (all screens) =====

  function setupFilterToggle() {
    var toggle = document.querySelector(SELECTORS.filterToggle);
    var sidebar = document.querySelector(SELECTORS.sidebar);

    if (!toggle || !sidebar) return;

    toggle.addEventListener('click', function () {
      var isOpen = sidebar.classList.contains('is-open');

      if (isOpen) {
        sidebar.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        sidebar.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  }

  // ===== Zoom-to-Cursor Effect =====

  function initParallax() {
    if (!gridEl) return;
    if (parallaxInitialized) return;
    parallaxInitialized = true;

    // Skip on touch devices
    if ('ontouchstart' in window) return;

    var SCALE = 1.05;
    var currentCard = null;
    var rafId = null;

    gridEl.addEventListener('mouseenter', function (e) {
      var card = e.target.closest('.cf-card');
      if (!card) return;
      currentCard = card;
      card.classList.add('is-hovering');
    }, true);

    gridEl.addEventListener('mousemove', function (e) {
      var card = e.target.closest('.cf-card');
      if (!card) return;

      if (card !== currentCard) {
        if (currentCard) resetCard(currentCard);
        currentCard = card;
        card.classList.add('is-hovering');
      }

      var rect = card.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;

      if (!rafId) {
        rafId = requestAnimationFrame(function () {
          var img = card.querySelector('.cf-card__media img');
          if (img) {
            img.style.transformOrigin = x + '% ' + y + '%';
            img.style.transform = 'scale(' + SCALE + ')';
          }
          rafId = null;
        });
      }
    }, true);

    gridEl.addEventListener('mouseleave', function (e) {
      var card = e.target.closest('.cf-card');
      if (!card) return;

      var related = e.relatedTarget;
      if (related && card.contains(related)) return;

      resetCard(card);
      if (card === currentCard) currentCard = null;
    }, true);

    function resetCard(card) {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      card.classList.remove('is-hovering');

      var img = card.querySelector('.cf-card__media img');
      if (img) {
        img.style.transform = '';
        img.style.transformOrigin = 'center center';
      }
    }
  }

  // ===== Swatch Preview (hover magnify) =====

  var swatchPreviewInitialized = false;

  function initSwatchPreview() {
    if (!gridEl || swatchPreviewInitialized) return;
    swatchPreviewInitialized = true;

    // Use event delegation on the grid
    gridEl.addEventListener('mouseenter', function (e) {
      var swatch = e.target.closest('.cf-swatch');
      if (!swatch) return;

      var fullSrc = swatch.dataset.fullSrc;
      var colorName = swatch.dataset.color;
      if (!fullSrc) return;

      var card = swatch.closest('.cf-card');
      if (!card) return;

      var preview = card.querySelector('.cf-card__preview');
      if (!preview) return;

      // Build preview content
      preview.innerHTML =
        '<img src="' + fullSrc + '" alt="' + (colorName || '') + '">' +
        '<span class="cf-card__preview-label">' + (colorName || '') + '</span>';

      preview.classList.add('is-active');
    }, true);

    gridEl.addEventListener('mouseleave', function (e) {
      var swatch = e.target.closest('.cf-swatch');
      if (!swatch) return;

      // Check if we moved to another swatch in the same card
      var related = e.relatedTarget;
      if (related && related.closest && related.closest('.cf-swatch')) return;

      var card = swatch.closest('.cf-card');
      if (!card) return;

      var preview = card.querySelector('.cf-card__preview');
      if (!preview) return;

      preview.classList.remove('is-active');
    }, true);
  }

  // ===== Color Swatches =====

  function buildColorSwatches(product) {
    var swatches = [];

    // Try to build swatches from Color option variants
    if (product.options && product.variants) {
      var colorIndex = -1;
      for (var i = 0; i < product.options.length; i++) {
        var name = product.options[i].name.toLowerCase();
        if (name === 'color' || name === 'colour' || name === 'colore') {
          colorIndex = i;
          break;
        }
      }

      if (colorIndex !== -1) {
        var seen = {};
        var optionKey = 'option' + (colorIndex + 1);
        for (var v = 0; v < product.variants.length; v++) {
          var variant = product.variants[v];
          var colorVal = variant[optionKey];
          if (!colorVal || seen[colorVal]) continue;
          seen[colorVal] = true;
          var thumbSrc = '';
          if (variant.featured_image && variant.featured_image.src) {
            thumbSrc = variant.featured_image.src;
            thumbSrc = thumbSrc + (thumbSrc.indexOf('?') > -1 ? '&' : '?') + 'width=80';
          }
          swatches.push({ color: colorVal, src: thumbSrc });
        }
      }
    }

    // Fallback: if no color swatches, show single thumbnail from first variant featured image
    if (swatches.length === 0 && product.variants) {
      for (var fi = 0; fi < product.variants.length; fi++) {
        if (product.variants[fi].featured_image && product.variants[fi].featured_image.src) {
          var fSrc = product.variants[fi].featured_image.src;
          swatches.push({
            color: product.title,
            src: fSrc + (fSrc.indexOf('?') > -1 ? '&' : '?') + 'width=80'
          });
          break;
        }
      }
    }

    if (swatches.length === 0) return '';

    var html = '<div class="cf-card__swatches">';
    for (var s = 0; s < swatches.length; s++) {
      var fullSrc = swatches[s].src ? swatches[s].src.replace(/width=80/, 'width=600') : '';
      if (swatches[s].src) {
        html += '<span class="cf-swatch" title="' + escapeAttr(swatches[s].color) + '"' +
          ' data-full-src="' + escapeAttr(fullSrc) + '"' +
          ' data-color="' + escapeAttr(swatches[s].color) + '">' +
          '<img src="' + escapeAttr(swatches[s].src) + '" alt="' + escapeAttr(swatches[s].color) + '" loading="lazy">' +
          '</span>';
      } else {
        html += '<span class="cf-swatch cf-swatch--no-img" title="' + escapeAttr(swatches[s].color) + '"></span>';
      }
    }
    html += '</div>';
    return html;
  }

  // ===== Helpers =====

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function escapeAttr(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
})();
